import type { DailyBite, TodaySummary } from '@nelnela/contracts';
import type { TileTint, TileVariant } from './Tile';

/**
 * The decisions Today makes about the day's plan (M6a step 11), separated from the rendering so
 * they can be held still by a test.
 *
 * Every one of them is a rule about what the screen SAYS, and the rule that governs all of them
 * is M5's: **a fact, never debt**. Nothing here counts what was missed, and nothing here invents
 * a number the data cannot support.
 */

/**
 * Has the day's goal been met?
 *
 * Deliberately not a clock. The evening state (`2c`) is about the goal being met, not about it
 * being evening — someone who does their fifteen minutes on the bus at eight in the morning has
 * finished, and Today should stop asking. There is no evening-boundary setting in M6a and this
 * is why one is not needed.
 */
export function isEvening(summary: Pick<TodaySummary, 'minutesDone' | 'goalMinutes'>): boolean {
  return summary.goalMinutes > 0 && summary.minutesDone >= summary.goalMinutes;
}

/** "+8 min" — how far past the goal, or null when it was only just met. */
export function overMinutes(
  summary: Pick<TodaySummary, 'minutesDone' | 'goalMinutes'>,
): number | null {
  const over = summary.minutesDone - summary.goalMinutes;
  return over > 0 ? over : null;
}

/**
 * The icon square's tint per tile kind. One axis per colour, never decoration (design README §5):
 * ember is the video and the ear, sky is the deliberate practice, neutral is reading.
 */
export function tintFor(activityType: string): TileTint {
  switch (activityType) {
    case 'watch':
    case 'listen':
      return 'ember';
    case 'review':
    case 'lesson':
      return 'sky';
    default:
      return 'neutral';
  }
}

/** Which shape a tile takes. The half-row pair is `2b`'s; the receipt is `3e`'s. */
export function variantFor(
  tile: DailyBite['plan'][number],
  opts: { evening: boolean },
): TileVariant {
  if (opts.evening) return 'completed';
  if (tile.activityType === 'review' && tile.completedActivityId !== null) return 'receipt';
  // A culture note is a minute; it takes the same compact half-row a listen tile does rather than
  // a full card, which would promise more than a minute of reading.
  if (
    tile.activityType === 'listen' ||
    tile.activityType === 'lesson' ||
    tile.activityType === 'culture'
  )
    return 'compact';
  return 'default';
}

/**
 * The receipt's second line (`3e`): "back in 4 days · nothing fading".
 *
 * `days` is the soonest `due_at` among the items just reviewed, which only exists after the
 * fold has run. `exerciseAnswer` kicks a fold rather than waiting for the quarter-hourly cron,
 * but that still takes seconds to a minute — so until it lands the line says "back soon", which
 * is true, rather than a number that would be wrong.
 */
export function receiptLine(daysUntilDue: number | null): string {
  if (daysUntilDue === null) return 'back soon · nothing fading';
  if (daysUntilDue <= 0) return 'back today · nothing fading';
  if (daysUntilDue === 1) return 'back tomorrow · nothing fading';
  return `back in ${daysUntilDue} days · nothing fading`;
}

/** "Review: 6 kept" (`3e`). The flip is the only celebration — a receipt, not confetti. */
export function receiptTitle(kept: number): string {
  return `Review: ${kept} kept`;
}

/**
 * The tiles Today actually renders, in plan order, minus the ones swiped away.
 *
 * A dismissed tile leaves no trace at all — no gap, no "dismissed" state, no undo. "Not today"
 * records nothing (contract), and a slot that still showed something would make the swipe feel
 * like it had not worked.
 */
export function visibleTiles(plan: DailyBite['plan']): DailyBite['plan'] {
  return plan.filter((t) => !t.dismissed);
}

/**
 * The pair of tiles that share a half-row (`2b`: Listen and One pattern side by side).
 *
 * Returned as a grouping over the visible tiles rather than as a flag on each, because "these
 * two go in one row" is a fact about the pair. A lone compact tile takes the full width rather
 * than half of it with a hole beside it.
 */
export interface TileRow {
  kind: 'full' | 'half';
  tiles: DailyBite['plan'];
}

export function tileRows(plan: DailyBite['plan'], opts: { evening: boolean }): TileRow[] {
  const rows: TileRow[] = [];
  let pending: DailyBite['plan'] = [];
  const flush = (): void => {
    if (pending.length === 0) return;
    rows.push({ kind: pending.length === 2 ? 'half' : 'full', tiles: pending });
    pending = [];
  };
  for (const tile of visibleTiles(plan)) {
    if (variantFor(tile, opts) === 'compact') {
      pending.push(tile);
      if (pending.length === 2) flush();
    } else {
      flush();
      rows.push({ kind: 'full', tiles: [tile] });
    }
  }
  flush();
  return rows;
}

/**
 * The English overlay's opacity on the watch tile's still (`2d`).
 *
 * The raw reveal level, with no floor — unlike Watch's own EN line, which floors at 0.35 so
 * `show` mode stays readable. Here the point is the meter itself: "at 0% the line is simply
 * gone. That's graduation." The Georgian above it never fades.
 */
export function subtitleOpacity(revealLevel: number): number {
  return Number.isFinite(revealLevel) ? Math.min(1, Math.max(0, revealLevel)) : 0.9;
}
